/**
 * Processes catalogue page.
 *
 * Lists every process (table). Row click navigates to /processes/:id (graph view).
 * Header has a "New process" button → opens a Dialog with the minimum required fields.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workflow, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useProcesses, useCreateProcess } from '../hooks';
import type { ProcessRow } from '../types';

const CATEGORIES = ['Sales', 'Operations', 'Quality', 'Finance', 'RnD', 'HR', 'IT'];

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  active: 'default',
  deprecated: 'outline',
  archived: 'destructive',
};

export default function ProcessesPage() {
  const navigate = useNavigate();
  const { data: processes, isLoading, error } = useProcesses({});
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Workflow className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Process maps with procedures, decisions, and KPI-tracked steps.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> New process
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right tabular-nums">Version</TableHead>
              <TableHead className="text-right tabular-nums">Steps</TableHead>
              <TableHead className="text-right tabular-nums">Edges</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            )}
            {error && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-destructive">
                Failed to load processes.
              </TableCell></TableRow>
            )}
            {!isLoading && !error && (processes || []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                No processes yet. Click "New process" to create your first one.
              </TableCell></TableRow>
            )}
            {(processes || []).map((p: ProcessRow) => (
              <TableRow
                key={p.id}
                className="cursor-pointer hover:bg-accent"
                onClick={() => navigate(`/processes/${p.id}`)}
              >
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{p.slug}</TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[p.status] || 'secondary'}>{p.status}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">v{p.version}</TableCell>
                <TableCell className="text-right tabular-nums">{p.step_count ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{p.edge_count ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewProcessDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}

function NewProcessDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Operations');
  const navigate = useNavigate();
  const createMutation = useCreateProcess();

  const reset = () => { setSlug(''); setTitle(''); setDescription(''); setCategory('Operations'); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const created = await createMutation.mutateAsync({ slug, title, description, category });
    reset();
    onClose();
    if (created?.id) navigate(`/processes/${created.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New process</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="np-title">Title <span className="text-destructive">*</span></Label>
            <Input id="np-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
          </div>
          <div>
            <Label htmlFor="np-slug">Slug <span className="text-destructive">*</span></Label>
            <Input id="np-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'))} placeholder="customer-onboarding" required />
          </div>
          <div>
            <Label htmlFor="np-category">Category <span className="text-destructive">*</span></Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="np-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="np-desc">Description</Label>
            <Textarea id="np-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending || !slug || !title}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
